/** Weave tab — rendered snapshot syntheses.
 *
 *  Vertical list of synthesis cards (person / engagement / weekly /
 *  org briefs) read from /api/weave. Each card shows the subject, the
 *  synthesized paragraph, and linkable source-evidence chips. Filter
 *  by kind. "Refresh" forces an on-demand run (write-when-asked).
 *
 *  Brand voice: "weave", "synthesize" — never "brain". */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, RefreshCw, Loader2, Users, CalendarClock, CalendarRange, Building2 } from "lucide-react";
import { getCsrfToken } from "../../lib/auth";

interface Evidence {
  kind: string;
  id: string;
  label: string;
}
interface Synthesis {
  id: string;
  scope: string;
  kind: string;
  subjectKey: string;
  subject: { title?: string; person_id?: string; engagement_id?: string; [k: string]: unknown };
  body: string;
  evidence: Evidence[];
  computedAt: string;
  model: string | null;
}
interface WeaveResponse {
  items: Synthesis[];
  counts: Record<string, number>;
}

const KINDS: Array<{ id: string; label: string; Icon: typeof Users }> = [
  { id: "person_brief", label: "People", Icon: Users },
  { id: "engagement_brief", label: "Engagements", Icon: CalendarClock },
  { id: "weekly_summary", label: "This week", Icon: CalendarRange },
  { id: "org_brief", label: "Orgs", Icon: Building2 },
];

function csrfHeaders(): Record<string, string> {
  const t = getCsrfToken();
  return t ? { "X-CSRF-Token": t } : {};
}

export function Weave() {
  const [items, setItems] = useState<Synthesis[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [activeKind, setActiveKind] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (activeKind) params.set("kind", activeKind);
      const res = await fetch(`/api/weave?${params.toString()}`, {
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      // Normalize at the boundary: a bare `as` cast lets an error
      // envelope or a future shape slip past and crash render on
      // items.length / Object.values(counts). Coerce to safe types.
      const data = (await res.json()) as Partial<WeaveResponse>;
      setItems(Array.isArray(data.items) ? data.items : []);
      setCounts(data.counts && typeof data.counts === "object" ? data.counts : {});
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setItems([]);
    }
  }, [activeKind]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRefresh = useCallback(async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await fetch(`/api/weave/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
        body: JSON.stringify({ max: 20 }),
      });
      if (!res.ok) throw new Error(`Run failed (${res.status})`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  }, [load]);

  const total = useMemo(
    () => Object.values(counts).reduce((a, b) => a + b, 0),
    [counts],
  );

  return (
    <section className="space-y-3">
      <div className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 sm:p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 inline-flex items-center gap-1.5 mr-1">
            <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            Weave
          </h2>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            What Memso has synthesized across people, engagements, and your week.
          </span>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={running}
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/40 disabled:opacity-50"
          >
            {running ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            {running ? "Synthesizing…" : "Refresh"}
          </button>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap mt-3">
          <KindChip
            label={`All${total ? ` · ${total}` : ""}`}
            active={activeKind === null}
            onClick={() => setActiveKind(null)}
          />
          {KINDS.map((k) => (
            <KindChip
              key={k.id}
              label={`${k.label}${counts[k.id] ? ` · ${counts[k.id]}` : ""}`}
              active={activeKind === k.id}
              onClick={() => setActiveKind(k.id)}
              Icon={k.Icon}
            />
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {items === null && (
        <div className="space-y-2" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-28 rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 animate-pulse"
              style={{ animationDelay: `${i * 90}ms` }}
            />
          ))}
        </div>
      )}

      {items !== null && items.length === 0 && (
        <div className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-8 text-center">
          <div className="inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-400 to-fuchsia-500 items-center justify-center text-white shadow-md mb-3">
            <Sparkles className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
            Nothing synthesized yet.
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
            Hit Refresh and Memso will weave what it knows about your people,
            engagements, and week into short briefs.
          </p>
        </div>
      )}

      {items !== null && items.length > 0 && (
        <ul className="space-y-2.5">
          {items.map((s) => (
            <SynthesisCard key={s.id} s={s} />
          ))}
        </ul>
      )}
    </section>
  );
}

function SynthesisCard({ s }: { s: Synthesis }) {
  const kindMeta = KINDS.find((k) => k.id === s.kind);
  const Icon = kindMeta?.Icon ?? Sparkles;
  const title = (s.subject?.title as string) || s.subjectKey;
  return (
    <li className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="inline-flex w-8 h-8 rounded-xl bg-violet-100 dark:bg-violet-900/40 items-center justify-center text-violet-600 dark:text-violet-300 flex-shrink-0">
          <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
            {title}
          </div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
            {kindMeta?.label ?? s.kind}
            {s.scope && s.scope !== "*" ? ` · ${s.scope}` : ""}
          </div>
        </div>
      </div>
      <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
        {s.body}
      </p>
      {s.evidence && s.evidence.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap pt-1">
          {s.evidence.slice(0, 6).map((e, i) => {
            const to =
              e.kind === "person" && s.subject?.person_id
                ? `/explore/people`
                : e.kind === "engagement"
                  ? `/engagements`
                  : null;
            const chip = (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                {e.label}
              </span>
            );
            return to ? (
              <Link key={`${e.kind}-${e.id}-${i}`} to={to} className="hover:opacity-80">
                {chip}
              </Link>
            ) : (
              <span key={`${e.kind}-${e.id}-${i}`}>{chip}</span>
            );
          })}
        </div>
      )}
    </li>
  );
}

function KindChip({
  label,
  active,
  onClick,
  Icon,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  Icon?: typeof Users;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? "bg-violet-600 text-white border-violet-600"
          : "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700"
      }`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
}

export default Weave;
